import AuthService from '../services/auth.service';

/**
 * Per-admin permission tags in the panel (spec 131).
 *
 * `GET /useradmin/me` returns the STORED list of the logged-in admin
 * (`permissions: string[]`, may contain `*`). This module caches that response
 * at module level — same pattern as `refundStatusColorsMixin` — so the router,
 * the drawer and the views share a single request, and computes the effective
 * tags with the same rule as the backend.
 *
 * The UI only mirrors the server: hiding something here is convenience, the
 * backend still enforces every tag.
 */

/** Wildcard tag: Super admin. */
export const SUPER_ADMIN_TAG = '*';

let cachedResult = null;
let cachedToken = null;
let inFlightRequest = null;

function currentToken() {
  return localStorage.getItem('accessToken');
}

/**
 * Pure rule shared by `can()` and the views that keep the list in reactive data.
 * @param {string[]|null|undefined} granted - stored list from `/me`.
 * @param {string} tag
 * @returns {boolean}
 */
export function hasPermission(granted, tag) {
  const list = Array.isArray(granted) ? granted : [];
  // The Super admin wildcard grants every tag.
  return list.includes(tag) || list.includes(SUPER_ADMIN_TAG);
}

/**
 * Any-of rule: whether `granted` holds at least one of `tags`. A single tag
 * string behaves exactly like `hasPermission`; an empty list grants nothing.
 * @param {string[]|null|undefined} granted - stored list from `/me`.
 * @param {string|string[]} tags - one tag, or alternatives (e.g. `X` and `X-own`).
 * @returns {boolean}
 */
export function hasAnyPermission(granted, tags) {
  const list = Array.isArray(tags) ? tags : [tags];
  return list.some((tag) => hasPermission(granted, tag));
}

/**
 * Loads `/me` once per session token and caches the successful result.
 * Concurrent callers share the in-flight request. A failed load is not cached,
 * so the next caller retries.
 *
 * @param {{ refresh?: boolean }} [options] - `refresh` forces a new request
 *   (used when landing on `/`, i.e. right after login).
 * @returns {Promise<{ status: 'ok', data: object } | { status: 'error', redirectTo?: string }>}
 *   The same shape as `AuthService.getUserData()`.
 */
export async function loadPermissions({ refresh = false } = {}) {
  const token = currentToken();
  // A different token means a different session (re-login after expiry):
  // the previous admin's permissions must never be reused.
  if (cachedResult && !refresh && cachedToken === token) return cachedResult;
  if (!inFlightRequest) {
    inFlightRequest = AuthService.getUserData()
      .then((res) => {
        if (res.status === 'ok' && res.data) {
          cachedResult = res;
          cachedToken = token;
        } else {
          cachedResult = null;
          cachedToken = null;
        }
        return res;
      })
      .finally(() => {
        inFlightRequest = null;
      });
  }
  return inFlightRequest;
}

/** Stored permission list of the logged-in admin, or `[]` before loading. */
export function getGrantedPermissions() {
  const permissions = cachedResult && cachedResult.data && cachedResult.data.permissions;
  return Array.isArray(permissions) ? permissions : [];
}

/** Id of the logged-in admin from the cached `/me`, or `null` before loading. */
export function getCurrentAdminId() {
  return (cachedResult && cachedResult.data && cachedResult.data.id) || null;
}

/**
 * Whether the logged-in admin holds `tag`, or any of the tags when given a
 * list (any-of, e.g. `['service-records:read', 'service-records:read-own']`).
 * Requires `loadPermissions()` first.
 * @param {string|string[]} tag
 * @returns {boolean}
 */
export function can(tag) {
  return hasAnyPermission(getGrantedPermissions(), tag);
}

/**
 * `can` for an action on one record that has an "own records" variant
 * (`<tag>-own`, spec 132): the general tag allows any record, the `-own` tag
 * only the ones `ownerId` says the logged-in admin created.
 * @param {string} tag - the general tag, e.g. `quotes:update`.
 * @param {string|null|undefined} ownerId - `users-admin.id` of the record's author.
 * @returns {boolean}
 */
export function canOnRecord(tag, ownerId) {
  if (can(tag)) return true;
  return can(`${tag}-own`) && Boolean(ownerId) && ownerId === getCurrentAdminId();
}

/**
 * Users-section tag that grants the same action from the 360 profile, keyed by
 * the tag of the section that owns the action (spec 132, 2026-11-03). The
 * backend accepts either one on the action's route.
 */
export const USER_360_ACTION_TAGS = {
  'pets:update': 'users:edit-pet',
  'memberships:update': 'users:update-memberships',
  'memberships:change-status': 'users:change-membership-status',
  'shelter-pets:update': 'users:manage-sponsorship',
  'co-tutors:create': 'users:create-co-tutors',
  'co-tutors:update': 'users:update-co-tutors',
  'co-tutors:view-documents': 'users:view-co-tutor-documents'
};

/**
 * `can` for an action reachable from the 360 profile: its own tag or the
 * Users-section tag in `USER_360_ACTION_TAGS`.
 * @param {string} tag - the tag of the section that owns the action.
 * @returns {boolean}
 */
export function canAct(tag) {
  const alternative = USER_360_ACTION_TAGS[tag];
  return can(alternative ? [tag, alternative] : tag);
}

/**
 * Whether a `permission` declaration (a tag or a list of alternative tags)
 * names at least one tag. Used by the menu and the router: without one, the
 * page is Super admin only.
 * @param {string|string[]|null|undefined} permission
 * @returns {boolean}
 */
export function isPermissionDeclared(permission) {
  return Array.isArray(permission) ? permission.length > 0 : Boolean(permission);
}

/** Whether the logged-in admin has `*`. Requires `loadPermissions()` first. */
export function isSuperAdmin() {
  return getGrantedPermissions().includes(SUPER_ADMIN_TAG);
}

const PERMISSION_ERROR_MESSAGES = {
  PERMISSION_DENIED: 'No tienes permiso para realizar esta acción.',
  CANNOT_EDIT_OWN_PERMISSIONS: 'No puedes modificar tus propios permisos.',
  UNKNOWN_PERMISSION: 'Uno de los permisos no existe. Recarga la página e inténtalo nuevamente.',
  LAST_PERMISSION_HOLDER:
    'No se puede: debe quedar al menos un administrador activo con acceso total.',
  MISSING_VIEW_PERMISSION:
    'Cada acción necesita el permiso de "Ver" de su sección. Marca "Ver" en las secciones indicadas.'
};

/**
 * Spanish message for the permission errors of the backend (specs 131/132).
 * The code comes in `code`, or as the message itself on older error shapes.
 * @param {{ code?: string|null, message?: string|null }} res
 * @param {string} fallback - message when the error is not a permission one.
 * @returns {string}
 */
export function permissionErrorMessage(res, fallback) {
  const code = (res && (res.code || res.message)) || '';
  return PERMISSION_ERROR_MESSAGES[code] || fallback;
}

/** Drops the cache. Called on logout. */
export function resetPermissions() {
  cachedResult = null;
  cachedToken = null;
  inFlightRequest = null;
}
